import React, { useState } from 'react';
import { Container, Paper, Box, Stepper, Step, StepLabel } from '@mui/material';
import '../awsManual.css';
import CreateCustomPolicy from './Steps/CreateCustomPolicy';
import CreateRole from './Steps/CreateRole';
import CreateS3Bucket from './Steps/CreateS3Bucket';
import CreateCostUsageReport from './Steps/CreateCostUsageReport';
import TitleHeader from '../../../../components/MasterComponent/TitleHeader/TitleHeader';
import CompleteAccountSetup from './Steps/CompleteManualAccountSetup';
import StepTitle from './StepTitle';

enum OnboardingStep {
  CREATE_CUSTOM_POLICY = 0,
  CREATE_ROLE = 1,
  CREATE_S3_BUCKET = 2,
  CREATE_COST_USAGE_REPORT = 3,
  COMPLETE_SETUP = 4,
}

const AwsAccountOnboarding: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<OnboardingStep>(
    OnboardingStep.CREATE_CUSTOM_POLICY
  );
  const [completedSteps, setCompletedSteps] = useState<boolean[]>([
    false,
    false,
    false,
    false,
    false,
  ]);

  const [s3BucketName, setS3BucketName] = useState<string>('');

  const handleStepComplete = (step: OnboardingStep) => {
    const newCompletedSteps = [...completedSteps];
    newCompletedSteps[step] = true;
    setCompletedSteps(newCompletedSteps);

    if (step < OnboardingStep.COMPLETE_SETUP) {
      setCurrentStep(step + 1);
    } else {
      console.log('Onboarding completed');
    }
  };

  const handleBack = () => {
    if (currentStep > OnboardingStep.CREATE_CUSTOM_POLICY) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Handle account number for S3 bucket naming
  const handleAccountNumberUpdate = (number: string) => {
    if (number && number.length === 12) {
      setS3BucketName(`cfx-cost-usage-${number}-us-east-1`);
    }
  };

  // steps data for the stepper
  const getSteps = () => {
    const steps = [
      { label: 'Create Custom Policy' },
      { label: 'Create Role' },
      { label: 'Create S3 Bucket' },
      { label: 'Create Cost & Usage Report' },
      { label: 'Complete Onboarding' },
    ];

    return steps.map((step, index) => ({
      ...step,
      completed: completedSteps[index],
    }));
  };

  // Get current step title
  const getCurrentStepTitle = () => {
    const titles = [
      'Create Custom Policy',
      'Create Role',
      'Create S3 Bucket',
      'Create Cost & Usage Report',
      'Complete Account Onboarding',
    ];
    return titles[currentStep];
  };

  // Data for the step components
  const renderCurrentStep = () => {
    switch (currentStep) {
      case OnboardingStep.CREATE_CUSTOM_POLICY:
        return (
          <CreateCustomPolicy
            onComplete={() =>
              handleStepComplete(OnboardingStep.CREATE_CUSTOM_POLICY)
            }
          />
        );
      case OnboardingStep.CREATE_ROLE:
        return (
          <CreateRole
            onComplete={() => handleStepComplete(OnboardingStep.CREATE_ROLE)}
            onBack={handleBack}
          />
        );
      case OnboardingStep.CREATE_S3_BUCKET:
        return (
          <CreateS3Bucket
            onComplete={() =>
              handleStepComplete(OnboardingStep.CREATE_S3_BUCKET)
            }
            onBack={handleBack}
            onAccountNumberUpdate={handleAccountNumberUpdate}
          />
        );
      case OnboardingStep.CREATE_COST_USAGE_REPORT:
        return (
          <CreateCostUsageReport
            onComplete={() =>
              handleStepComplete(OnboardingStep.CREATE_COST_USAGE_REPORT)
            }
            onBack={handleBack}
            s3BucketName={s3BucketName}
          />
        );
      case OnboardingStep.COMPLETE_SETUP:
        return <CompleteAccountSetup onBack={handleBack} />;
      default:
        return null;
    }
  };

  return (
    <Container className="onboardingContainer">
      <TitleHeader>AWS Manual Account Setup</TitleHeader>
      <Paper elevation={2} className="onboardingContent pb-10">
        <Stepper
          activeStep={currentStep}
          alternativeLabel
          className="stepperContainer"
        >
          {getSteps().map((step, index) => (
            <Step key={index} completed={step.completed}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <StepTitle stepNumber={currentStep + 1} title={getCurrentStepTitle()} />
        <Box className="stepContent">{renderCurrentStep()}</Box>
      </Paper>
    </Container>
  );
};

export default AwsAccountOnboarding;
